import { GameId } from '@bg-shared/enums/GameId';
import { analyticsApi } from '@bg-shared/business/Analytics';
import { totalBetAmountEntity } from '@bg-shared/business/TotalBetAmount';
import { ChipActionType } from '../enums';
import { IChipAction, IChipAmounts, ISectorChoiceAction } from '../interfaces';
import { optionIDConverter } from '../utils/optionIDConverter';
import { casinoSoundsService } from './CasinoSounds.service';
import {
    casinoBetActionsEntity,
    casinoBetAmountsEntity,
    casinoActiveOptionsClassesEntity,
    casinoLatestPayinEntity,
    selectedChipStore,
} from '../store';
import { CasinoValidationService } from './CasinoValidation.service';
import { CasinoBettingApi } from './CasinoBetting.api';

export class CasinoChipsService {
    protected readonly betActionsStore: ReturnType<typeof casinoBetActionsEntity.store>;

    protected readonly betAmountsStore: ReturnType<typeof casinoBetAmountsEntity.store>;

    protected readonly latestPayinStore: ReturnType<typeof casinoLatestPayinEntity.store>;

    protected readonly totalBetAmountStore: ReturnType<typeof totalBetAmountEntity.store>;

    protected readonly activeOptionsClassesStore: ReturnType<
        typeof casinoActiveOptionsClassesEntity.store
    >;

    constructor(
        protected readonly gameId: GameId,
        private readonly validationService: CasinoValidationService,
        private readonly bettingApi: CasinoBettingApi,
    ) {
        this.betActionsStore = casinoBetActionsEntity.store(gameId);
        this.latestPayinStore = casinoLatestPayinEntity.store(gameId);
        this.betAmountsStore = casinoBetAmountsEntity.store(gameId);
        this.totalBetAmountStore = totalBetAmountEntity.store(gameId);
        this.activeOptionsClassesStore = casinoActiveOptionsClassesEntity.store(gameId);
    }

    protected handleNextAction(actions: IChipAction | IChipAction[]): void {
        this.betActionsStore.add(actions);
        this.betAmountsStore.updateFromActions(this.betActionsStore.value);

        this.bettingApi.placeStagedBets();
    }

    public addChips(optionItemIDs: string[]): void {
        const optionsArray = optionItemIDs.map((optionItemID) => ({
            optionItemID,
            additionalAmount: selectedChipStore.value,
        }));

        const validationErrors = this.validationService.getAddValidationErrors(optionsArray) || {};

        const validOptionItems = optionItemIDs
            .filter(
                (optionId) =>
                    !this.validationService.isBlockedByValidation(validationErrors[optionId]),
            )
            .map((filteredOptionId) => ({
                id: filteredOptionId,
                amount: validationErrors[filteredOptionId]
                    ? validationErrors[filteredOptionId].diffAmount
                    : selectedChipStore.value,
            }));

        if (validOptionItems.length > 0) {
            if (validOptionItems.length > 1) {
                casinoSoundsService.playPlaceChips();
            } else {
                casinoSoundsService.playPlaceChip();
            }

            this.handleNextAction({
                type: ChipActionType.ADD,
                optionItemIDs: validOptionItems.map((option) => option.id),
                amounts: validOptionItems.map((option) => option.amount),
                validated: false,
                pendingUndo: null,
            });
        }
    }

    public moveChip(optionItemID: string, moveFromOptionItemID: string): void {
        analyticsApi.trackCasinoMove(this.gameId);

        const validationError = this.validationService.getMoveValidationErrors({
            optionItemID,
            moveFromOptionItemID,
            additionalAmount: this.betAmountsStore.value[moveFromOptionItemID],
        });

        if (this.validationService.isBlockedByValidation(validationError)) {
            return;
        }

        casinoSoundsService.playPlaceChip();

        this.handleNextAction({
            type: ChipActionType.MOVE,
            optionItemIDs: [optionItemID],
            moveFromOptionItemID,
            diffAmount: validationError?.diffAmount,
            validated: false,
            pendingUndo: null,
        });
    }

    public removeChip(optionItemID: string): void {
        casinoSoundsService.playRemoveChip();

        this.handleNextAction({
            type: ChipActionType.REMOVE,
            optionItemIDs: [optionItemID],
            validated: false,
            pendingUndo: null,
        });
    }

    public doubleChips(): void {
        analyticsApi.trackCasinoDouble(this.gameId);

        const validationErrors = this.validationService.getDoubleValidationErrors();

        if (this.validationService.isBlockedByValidation(validationErrors)) {
            return;
        }

        const nextAction: IChipAction = {
            type: ChipActionType.DOUBLE,
            validated: false,
            pendingUndo: null,
        };

        if (Array.isArray(validationErrors)) {
            nextAction.diffAmount = validationErrors.reduce<IChipAmounts>(
                (acc, [optionItemID, error]) => {
                    if (error?.diffAmount || error?.isBlocking) {
                        acc[optionItemID] = error.diffAmount;
                    }
                    return acc;
                },
                {},
            );

            casinoSoundsService.playPlaceChips();
        }

        this.handleNextAction(nextAction);
    }

    public undoLastAction(): void {
        casinoSoundsService.playRemoveChip();

        analyticsApi.trackCasinoUndo(this.gameId);
        this.betActionsStore.undoOptimistic();
        this.betAmountsStore.updateFromActions(this.betActionsStore.value);
        this.bettingApi.placeStagedBets();
    }

    public repeatChips(repeatOnlyLast = false): void {
        analyticsApi.trackCasinoRepeat(this.gameId);

        const bets = !this.activeOptionsClassesStore.value.length
            ? this.latestPayinStore.value.recentBets
            : this.latestPayinStore.value.recentBets.filter((bet) =>
                  this.activeOptionsClassesStore.value.includes(bet.oddClass),
              );
        const filteredBets = repeatOnlyLast ? [bets[bets.length - 1]] : bets;

        if (!filteredBets.length || !this.validationService.validateRepeat(filteredBets)) {
            return;
        }

        casinoSoundsService.playPlaceChips();

        this.handleNextAction(
            filteredBets.map((bet) => ({
                type: ChipActionType.REPEAT,
                amounts: [Number(bet.amount)],
                optionItemIDs: [optionIDConverter.create(bet.oddId, bet.items)],
                validated: false,
                pendingUndo: null,
            })),
        );
    }

    public chooseSector(
        params: Omit<ISectorChoiceAction, 'type'> | Omit<ISectorChoiceAction, 'type'>[],
    ): void {
        const paramsList = Array.isArray(params) ? params : [params];

        this.handleNextAction(
            paramsList.map((param) => ({
                type: ChipActionType.SECTOR_CHOICE,
                optionItemIDs: param.optionItemIDs,
                item: param.item,
                index: param.index,
                itemsCount: param.itemsCount,
            })),
        );
    }

    public reset(): void {
        this.betActionsStore.reset();
        this.betAmountsStore.reset();
        this.totalBetAmountStore.reset();
    }

    public clearBets(): void {
        this.reset();
        this.bettingApi.placeStagedBets();
    }
}
